import React, { FC, PropsWithChildren, useEffect, useRef } from 'react'
import { Flex, IconButton, Text } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { IconSystemChevronLeft } from '@moonpig/launchpad-assets'
import { useFindLocaleText } from '../../../../text-localisation'
import { Panel as SlidingMenuPanel } from '../SlidingMenu'
import { FilterCategory } from '../../../../services/types/services'
import { ClearButton } from '../../../ClearButton'
import { FilterSource } from '../../../../types'
import { FILTERS_MENU_HEADER_HEIGHT_PX } from '../../constants'

const StyledText = styled(Text)`
  ${s({
    textAlign: 'left',
    typography: { xs: 'typeMobileDisplay05', md: 'typeDesktopDisplay06' },
  })}
`

const StyledHeader = styled(Flex)`
  position: sticky;
  z-index: 1051;
  top: 0;
  left: 0;
  ${s({
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: FILTERS_MENU_HEADER_HEIGHT_PX,
    py: 3,
    pl: 3,
    pr: 6,
    borderBottom: 1,
    borderColor: 'colorBorder03',
    bgcolor: 'colorBackground01',
  })}
`

type Props = {
  category: FilterCategory
  level: number
  onClose: () => void
  hideClearButton: boolean
}

export const FilterCategorySubMenu: FC<PropsWithChildren<Props>> = ({
  category,
  onClose,
  children,
  level,
  hideClearButton,
}) => {
  const localiseText = useFindLocaleText()
  const categoryBackButton = useRef<HTMLButtonElement>(null)
  const sidePanelId = `${category.id}-filter-category-panel`

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation()
    onClose()
  }

  useEffect(() => {
    /* istanbul ignore else */
    if (categoryBackButton.current) {
      categoryBackButton.current.focus()
    }
  }, [categoryBackButton])

  return (
    <SlidingMenuPanel level={level} id={sidePanelId}>
      <StyledHeader data-testid="filter-category-header">
        <IconButton
          ref={categoryBackButton}
          icon={IconSystemChevronLeft}
          onClick={handleBackButtonClick}
          label={localiseText('find.filters_menu_panel.close', category.label)}
        />
        <StyledText>{category.label}</StyledText>
        <ClearButton
          level={level}
          category={category}
          hide={hideClearButton}
          source={FilterSource.FiltersMenu}
        />
      </StyledHeader>
      {children}
    </SlidingMenuPanel>
  )
}
